import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { RentPriceItem } from '@/types/dto/rent-unit.dto';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

const METHOD = 'GET';
const ENDPOINT = 'unit/:id/rent-prices';

export const getQueryKeyGetRentPrices = (unitId: number | undefined) =>
  getQueryKey(METHOD, ENDPOINT, unitId ? { id: unitId } : undefined);

/**
 * Hook to fetch all rent prices (active and inactive) for a unit from Stripe
 * @param unitId The ID of the unit
 * @param enabled Whether the query should execute
 * @returns QueryResult containing array of RentPriceItem objects
 */
export const useGetRentPrices = (unitId: number | undefined, enabled = true) => {
  const { session } = useAuth();

  return useQuery({
    queryKey: getQueryKeyGetRentPrices(unitId),
    queryFn: async () => {
      if (!unitId) {
        return [];
      }
      try {
        const data = await handleApiCall<RentPriceItem[]>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { id: unitId.toString() })
        );

        if (!data || data.length === 0) {
          return [];
        }

        // Convert amounts from cents to dollars for display
        return data.map(price => ({
          ...price,
          amount: price.amount / 100,
        }));
      } catch (err) {
        enqueueSnackbar('Failed to fetch rent prices.', { variant: 'error' });
        return [];
      }
    },
    enabled: enabled && !!session && !!unitId,
  });
};
